import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import type { User } from '@supabase/supabase-js';
import { Button, CarouselDots, Modal } from '@/components/ui';
import { ProfileAvatar } from '@/components/domain/shared/ProfileAvatar';
import { clearInvitationToken } from '@/lib/invitationContext';
import { setActiveHouseholdPin } from '@/lib/active-household-pin';
import { useHouseholdStore } from '@/stores/householdStore';
import { useOnboardingStore } from '@/stores/onboardingStore';
import { useUserStore } from '@/stores/userStore';
import { useAuth } from '@/hooks';
import {
  acceptInvitation,
  completeInvitationOnboarding,
  declineInvitation,
  setInvitationOnboardingStep,
  type InvitationPreviewData,
  type InviteeOnboardingStep,
} from '@/services/invitation.service';
import type { InviteeOnboardingFlow as InviteeFlowKind } from '@/lib/onboardingFlow';
import { OnboardingFooterProvider } from './OnboardingFooterContext';
import { OnboardingLayout } from './OnboardingLayout';
import { StepProfileSetup } from './StepProfileSetup';
import {
  INVITEE_COPY,
  INVITEE_FALLBACK_ADMIN_NAME,
  INVITEE_FALLBACK_HOUSEHOLD_NAME,
} from './inviteeOnboardingCopy';

/**
 * Backend failure text is never shown to the invitee. `caught.message` here is
 * whatever the edge function or Postgres produced — "Edge Function returned a
 * non-2xx status code", "invitation_onboarding_not_available" — and it used to
 * render under the screen title as if it were governed product copy. Log it,
 * show a human sentence.
 */
function reportInviteeFlowError(action: string, caught: unknown): void {
  console.error(
    `invitee-onboarding: ${action} failed`,
    caught instanceof Error ? caught.message : String(caught),
  );
}

type FlowStep = 'acceptance' | InviteeOnboardingStep;

const FLOW_STEPS: FlowStep[] = [
  'acceptance',
  'demo1',
  'demo2',
  'demo3',
  'profile',
  'welcome',
];

const DEMO_VIDEOS: Record<InviteeFlowKind, readonly [string, string, string]> =
  {
    'invitee-editor': [
      '/onboarding/invitee-videos/canedit-demo1-v2.mp4',
      '/onboarding/invitee-videos/canedit-demo2-v1.mp4',
      '/onboarding/invitee-videos/all-demo3-v1.mp4',
    ],
    'invitee-viewer': [
      '/onboarding/invitee-videos/canview-demo1-v1.mp4',
      '/onboarding/invitee-videos/canview-demo2-v1.mp4',
      '/onboarding/invitee-videos/all-demo3-v1.mp4',
    ],
  };

function firstName(displayName: string | null): string {
  return displayName?.trim().split(/\s+/)[0] || INVITEE_FALLBACK_ADMIN_NAME;
}

function InviteeAcceptance({
  invitation,
  flow,
  accepted,
  busy,
  error,
  onAccept,
  onDecline,
}: {
  invitation: InvitationPreviewData;
  flow: InviteeFlowKind;
  accepted: boolean;
  busy: boolean;
  error: string | null;
  onAccept: () => void;
  onDecline: () => void;
}) {
  const adminName = firstName(invitation.inviter?.display_name ?? null);
  const householdName =
    invitation.household_name?.trim() || INVITEE_FALLBACK_HOUSEHOLD_NAME;

  return (
    // Figma 9644:96875 (Can edit) / 9706:127456 (Can view): the avatar and the
    // title share ONE left alignment axis (items-start, x=0 at the page
    // margin), the pair is TOP-anchored with a 20px gap, and the button stack
    // is pinned to the bottom 16px apart. It was centering the 160px avatar
    // over a left-aligned headline, on a vertically-centered axis.
    <div className="flex flex-1 flex-col items-start">
      <div className="flex flex-1 flex-col items-start gap-5 py-10">
        <ProfileAvatar
          directUrl={invitation.inviter?.avatar_url}
          emoji={invitation.inviter?.avatar_emoji || '🦄'}
          color={invitation.inviter?.avatar_color || 'bg-emoji-1'}
          alt={`${adminName}'s profile`}
          size="2xl"
        />
        {/* No line clamp: Figma sets none, the frame just grows. */}
        <h1 className="w-full text-left text-[28px] leading-8 font-bold text-text-hero break-words">
          {INVITEE_COPY.acceptance.title(
            adminName,
            householdName,
            flow === 'invitee-editor',
          )}
        </h1>
        {error && (
          <p
            role="alert"
            className="text-sm text-text-error"
          >
            {error}
          </p>
        )}
      </div>

      {/* 16px (spacing/component/lg) between Accept and Decline — measured
          from the frame geometry (button 1 y=0 h=36, button 2 y=52). */}
      <div className="flex w-full flex-col gap-4">
        <Button
          variant="primary"
          fullWidth
          loading={busy}
          onClick={onAccept}
        >
          {INVITEE_COPY.acceptance.accept}
        </Button>
        {!accepted && (
          <Button
            variant="tertiary"
            fullWidth
            disabled={busy}
            onClick={onDecline}
          >
            {INVITEE_COPY.acceptance.decline}
          </Button>
        )}
      </div>
    </div>
  );
}

function InviteeDemo({
  step,
  flow,
}: {
  step: 'demo1' | 'demo2' | 'demo3';
  flow: InviteeFlowKind;
}) {
  const index = Number(step.slice(-1)) - 1;
  // Card 2 branches on access level (AC 4 of both stories names a different
  // one); cards 1 and 3 are shared. The recording below is per-role too, so a
  // viewer reads a viewer's card over a viewer's screen.
  const { title } = INVITEE_COPY.carousel.cards(flow === 'invitee-editor')[
    index
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col items-center gap-5">
      {/* Headline only — the invitee Demo frames put the recording directly
          under it, with no supporting line (YAR-367). */}
      <h1 className="w-full text-[28px] leading-8 font-bold text-text-hero">
        {title}
      </h1>
      <div className="flex min-h-0 w-full flex-1 justify-center">
        <div
          className="h-full max-w-full overflow-hidden rounded-3xl border-[1.5px] border-border-strong"
          style={{ aspectRatio: '394 / 874' }}
        >
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            src={DEMO_VIDEOS[flow][index]}
            className="h-full w-full object-cover object-top"
          />
        </div>
      </div>
      {/* Three dots, between the recording and the CTA. The invitee flow was
          the only carousel without them: they were read as a stale Figma
          artifact superseded by the global segmented bar, but the re-authored
          frames draw BOTH, and the shipped admin flow has carried dots all
          along (StepDemo). YAR-313. */}
      <CarouselDots
        count={3}
        active={index}
      />
    </div>
  );
}

function InviteeWelcome({
  householdName,
  busy,
  error,
  onComplete,
}: {
  householdName: string;
  busy: boolean;
  error: string | null;
  onComplete: () => void;
}) {
  return (
    // Figma 9704:112864 / 9706:127469: content is LEFT-aligned and
    // TOP-anchored with a tight 8px rhythm between all three elements, and the
    // CTA is pinned to the bottom. It was vertically centering everything with
    // a 32px gap.
    <div className="flex flex-1 flex-col">
      <div className="flex flex-1 flex-col items-start gap-2 py-10">
        <span
          className="text-7xl"
          role="img"
          aria-label="Celebration"
        >
          🎉
        </span>
        <div className="flex flex-col gap-2">
          <h1 className="text-[28px] leading-8 font-bold text-text-hero">
            {INVITEE_COPY.welcome.title(householdName)}
          </h1>
          {/* `text/content/hero` (#0d0d0d), not body (#2f2f2f) — the frame
              paints this supporting line the same tone as the headline
              (YAR-289). */}
          <p className="text-xl leading-7 text-text-hero">
            {INVITEE_COPY.welcome.body(householdName)}
          </p>
        </div>
        {error && (
          <p
            role="alert"
            className="text-sm text-text-error"
          >
            {error}
          </p>
        )}
      </div>
      <Button
        variant="primary"
        fullWidth
        loading={busy}
        onClick={onComplete}
      >
        {INVITEE_COPY.welcome.cta}
      </Button>
    </div>
  );
}

export function InviteeOnboardingFlow({
  user,
  invitation,
  flow,
  initialState,
}: {
  user: User;
  invitation: InvitationPreviewData;
  flow: InviteeFlowKind;
  initialState: 'pending' | 'accepted';
}) {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const initFromUser = useOnboardingStore((state) => state.initFromUser);
  const [step, setStep] = useState<FlowStep>(
    initialState === 'accepted'
      ? invitation.onboarding_step || 'demo1'
      : 'acceptance',
  );
  const [accepted, setAccepted] = useState(initialState === 'accepted');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [profileFooter, setProfileFooter] = useState<ReactNode>(null);
  const footerContextValue = useMemo(
    () => ({ setFooter: setProfileFooter }),
    [],
  );

  useEffect(() => {
    initFromUser(user.id, Intl.DateTimeFormat().resolvedOptions().timeZone);
  }, [initFromUser, user.id]);

  const moveTo = useCallback(
    async (nextStep: InviteeOnboardingStep) => {
      setBusy(true);
      setError(null);
      try {
        await setInvitationOnboardingStep(invitation.id, nextStep);
        setStep(nextStep);
      } catch (caught) {
        reportInviteeFlowError('set_step', caught);
        setError(INVITEE_COPY.errors.step);
      } finally {
        setBusy(false);
      }
    },
    [invitation.id],
  );

  const handleAccept = useCallback(async () => {
    if (accepted) {
      setStep('demo1');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await acceptInvitation(invitation.id);
      setAccepted(true);
      setStep('demo1');
    } catch (caught) {
      reportInviteeFlowError('accept', caught);
      setError(INVITEE_COPY.errors.accept);
    } finally {
      setBusy(false);
    }
  }, [accepted, invitation.id]);

  const handleDecline = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await declineInvitation(invitation.id);
      clearInvitationToken();
      await signOut();
      navigate('/', { replace: true });
    } catch (caught) {
      setDeclineOpen(false);
      reportInviteeFlowError('decline', caught);
      setError(INVITEE_COPY.errors.decline);
      setBusy(false);
    }
  }, [invitation.id, navigate, signOut]);

  const handleComplete = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await completeInvitationOnboarding(invitation.id);
      setActiveHouseholdPin(invitation.household_id);
      const role = flow === 'invitee-editor' ? 'EDITOR' : 'VIEWER';
      useUserStore.getState().setRole(role);
      // The RPC has just stamped users.onboarding_completed_at server-side.
      // Mirror it locally: the store is only refilled from the profile at
      // boot, and ProtectedRoute reads this flag on the very next render.
      // Left null, /today bounces to '/', which bounces back to /today
      // because the role above is not PRIMARY — a redirect loop that never
      // paints, i.e. a blank screen. COALESCE on the server keeps an
      // established parent's original date, so only fill a null here.
      if (useUserStore.getState().onboardingCompletedAt === null) {
        useUserStore
          .getState()
          .updateProfile({ onboardingCompletedAt: new Date().toISOString() });
      }
      useHouseholdStore
        .getState()
        .setActiveHousehold(invitation.household_id, role);
      clearInvitationToken();
      // Nothing typed during this flow may carry into the next one: the
      // profile answers are per household (YAR-485), and the admin flow
      // resets the same store when it finishes.
      useOnboardingStore.getState().resetStore();
      navigate('/today', { replace: true });
    } catch (caught) {
      reportInviteeFlowError('complete', caught);
      setError(INVITEE_COPY.errors.complete);
      setBusy(false);
    }
  }, [flow, invitation.household_id, invitation.id, navigate]);

  const currentIndex = FLOW_STEPS.indexOf(step);
  const progress = ((currentIndex + 1) / FLOW_STEPS.length) * 100;
  const householdName =
    invitation.household_name?.trim() || INVITEE_FALLBACK_HOUSEHOLD_NAME;

  // Acceptance and Welcome are chromeless: their frames (Figma 9644:96875 /
  // 9706:127456 Acceptance, 9704:112864 / 9706:127469 Welcome) draw content
  // straight from the top margin with no chevron and no bar. The flow used to
  // paint both on every step (QA on YAR-288 / YAR-289). Welcome in particular
  // must not offer Back — onboarding is already persisted as `welcome` and
  // the only way out is Go to Dashboard.
  const isChromeless = step === 'acceptance' || step === 'welcome';

  const handleBack = () => {
    const previous = FLOW_STEPS[currentIndex - 1];
    if (!previous) return;
    setError(null);
    setStep(previous);
  };

  let content: ReactNode;
  let footer: ReactNode = null;

  if (step === 'acceptance') {
    content = (
      <InviteeAcceptance
        invitation={invitation}
        flow={flow}
        accepted={accepted}
        busy={busy}
        error={error}
        onAccept={() => void handleAccept()}
        onDecline={() => setDeclineOpen(true)}
      />
    );
  } else if (step === 'demo1' || step === 'demo2' || step === 'demo3') {
    const nextStep: InviteeOnboardingStep =
      step === 'demo1' ? 'demo2' : step === 'demo2' ? 'demo3' : 'profile';
    content = (
      <>
        <InviteeDemo
          step={step}
          flow={flow}
        />
        {error && (
          <p
            role="alert"
            className="mt-3 text-center text-sm text-text-error"
          >
            {error}
          </p>
        )}
      </>
    );
    footer = (
      <Button
        variant="primary"
        fullWidth
        loading={busy}
        onClick={() => void moveTo(nextStep)}
      >
        {step === 'demo3' ? 'Continue' : 'Next'}
      </Button>
    );
  } else if (step === 'profile') {
    content = (
      <StepProfileSetup
        user={user}
        onComplete={() => void moveTo('welcome')}
        // The role is this household's, saved to this membership — it
        // starts empty here even if another household already has one
        // (YAR-485).
        householdId={invitation.household_id}
      />
    );
    footer = profileFooter;
  } else {
    content = (
      <InviteeWelcome
        householdName={householdName}
        busy={busy}
        error={error}
        onComplete={() => void handleComplete()}
      />
    );
  }

  return (
    <OnboardingFooterProvider value={footerContextValue}>
      <OnboardingLayout
        showBackButton={!isChromeless && currentIndex > 0}
        onBack={handleBack}
        showProgressBar={!isChromeless}
        progress={progress}
        stickyFooter={footer}
      >
        <div className="ph-no-capture flex min-h-0 flex-1 flex-col">
          {content}
        </div>
      </OnboardingLayout>

      <Modal
        open={declineOpen}
        onClose={() => setDeclineOpen(false)}
        ariaLabel={INVITEE_COPY.declineModal.title}
        bare
        dismissOnBackdrop={false}
      >
        {/* Figma 9704:112731 — card padding 20 (xl), 16 (lg) between the text
            block and the buttons, 6 (2xs) inside the text block, 12 (md)
            between the buttons. Title is Body-lg/MEDIUM 20/28, NOT an
            H4/semibold. Both lines are CENTERED. The title is
            `text/content/heading` (#1a1a1a) and the body `text/content/body`
            (#2f2f2f) — the title was body-toned until QA on YAR-288. */}
        <div className="flex flex-col gap-4 p-5">
          <div className="flex flex-col gap-1.5 text-center">
            <h2 className="text-xl leading-7 font-medium text-text-heading">
              {INVITEE_COPY.declineModal.title}
            </h2>
            <p className="text-base leading-6 text-foreground">
              {INVITEE_COPY.declineModal.body(
                firstName(invitation.inviter?.display_name ?? null),
              )}
            </p>
          </div>
          <div className="flex flex-col gap-3">
            {/* Outline, not a solid red slab: the frame draws the confirm as
                white with an error-toned border and label, and the app had the
                fill and the text the other way round (QA on YAR-289 —
                "colors are inverted"). The red is `#a82222`, not `danger`
                `#c94040`. */}
            <Button
              variant="destructiveOutline"
              fullWidth
              loading={busy}
              onClick={() => void handleDecline()}
            >
              {INVITEE_COPY.declineModal.confirm}
            </Button>
            <Button
              variant="tertiary"
              fullWidth
              disabled={busy}
              onClick={() => setDeclineOpen(false)}
            >
              {INVITEE_COPY.declineModal.cancel}
            </Button>
          </div>
        </div>
      </Modal>
    </OnboardingFooterProvider>
  );
}
